import { Inject, Injectable } from '@angular/core';
import {
  DwCacheService,
  DwOrmDataService,
  DwOrmDataServiceToken,
  DwSecurityTopics
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AppMetaData,
  AppMetaDataItemNames,
  SubscriptionFeatureKeys
} from '../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../admin/services/admin-manager.service';
import { SchoolFeatures } from '../../schools/models/school-features.model';

@Injectable({ providedIn: 'root' })
export class UnlimitedCourseAccessService {
  constructor(
    @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
    private dwCacheService: DwCacheService,
    private adminManagerService: AdminManagerService
  ) { }

  hasAccess(schoolFeatures: SchoolFeatures): Observable<boolean> {
    const userId = this.adminManagerService.getAviatorUser()?.user?.UserId;
    const tierId = schoolFeatures?.SubscriptionTierId;
    const featureEnabled = schoolFeatures?.Features
      ?.includes(SubscriptionFeatureKeys.UnlimitedCourseAccess);

    if (!featureEnabled || !userId || !tierId) {
      return of(false);
    }

    const invalidationTopics = [
      AppMetaDataItemNames.UserSubscriptionRole,
      AppMetaDataItemNames.SubscriptionTierRole
    ].map(item => DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(item));
    invalidationTopics.push(DwSecurityTopics.SECURITY_CHANGED);

    const roleCacheKey = (schoolFeatures.SubscriptionTierRoleIds || [])
      .slice()
      .sort((left, right) => left - right)
      .join(',');

    return this.dwCacheService.get(
      `UnlimitedCourseAccess:${userId}:${tierId}:${roleCacheKey}`,
      this.loadAccess(userId, schoolFeatures.SubscriptionTierRoleIds || []),
      null,
      invalidationTopics
    );
  }

  private loadAccess(userId: number, allowedRoleIds: number[]): Observable<boolean> {
    if (!allowedRoleIds.length) {
      return of(false);
    }

    const userRolesQuery = AppMetaData.UserSubscriptionRole.CreateQueryBuilder();

    userRolesQuery.addFilterAnd(filter => filter.Equal(attribute => attribute.UserId, userId));
    
    const allowedRoles = new Set(allowedRoleIds);

    return this.dwOrmDataService.executeQuery(userRolesQuery)
      .pipe(map(userRoles => userRoles.some(role => role.SubscriptionRoleId != null
        && allowedRoles.has(role.SubscriptionRoleId))));
  }
}
